import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

import { useChatbotStore } from '@/stores/chatbot/chatbotStore';
import { useUserStore } from '@/stores/user/userStore';
import ChatbotActions from '@/components/Chatbot/ChatbotActions';
import Button from '@/shared/components/UI/Button';

import chatbotNavItems from '@/components/Chatbot/Details/NavItems';
import { checkTeamManagementPermission, checkFeatureAvailability } from '@/helpers/tier/featureCheck';
import DModalShareChatbot from '@/components/Chatbot/DModalShareChatbot';
import DConfirmationModal from '@/components/Global/DConfirmationModal';
import useModalStore from '@/stores/modal/modalStore';
import { LLM_MODEL_DEFAULT } from '@/constants';
import { deleteChatbot } from '@/services/chatbot.service';
import { getUserProfile } from '@/services/user.service';
import KnowledgeTooltip from '@/components/KnowledgeTooltip';
import useKnowledgeTooltipStore from '@/stores/knowledgeTooltip/knowledgeTooltipStore';
import IconWrapper from '@/shared/components/UI/IconWrapper';


import MemoryIcon from '@/shared/icons/MemoryIconV3';
import PreviewIcon from '@/shared/icons/PreviewIcon';
import GraphIcon from '@/shared/icons/GraphIcon';
import HubIcon from '@/shared/icons/HubIcon';
import SafetyIcon from '@/shared/icons/SafetyIcon';
import DashboardIcon from '@/shared/icons/DashboardIcon';
import ConfigurationIcon from '@/shared/icons/ConfigurationIcon';
import BehaviorIcon from '@/shared/icons/BehaviorIcon';
import VoiceIcon from '@/shared/icons/VoiceIcon';
import PowerUpsIcon from '@/shared/icons/PowerUpsIcon';
import IntegrationIcon from '@/shared/icons/IntegrationIcon';
import StylingIcon from '@/shared/icons/StylingIcon';
import LeftArrowIcon from '@/shared/icons/LeftArrowIcon';
import ShareLineIcon from '@/shared/icons/ShareLineIcon';
import CreditsAndUpgrade from '@/v3-dashboard/components/CreditsAndUpgrade';


const DChatbotSidebar = ({ closeMobileNav }) => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const setCurrentStep = useChatbotStore((state) => state.setCurrentStep);
  const selectedChatbot = useChatbotStore((state) => state.selectedChatbot);
  const setUser = useUserStore((state) => state.setUser);
  const auth = useUserStore((state) => state.auth);
  const user = useUserStore((state) => state.user);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [groups, setGroups] = useState({});
  const [showShareModal, setShowShareModal] = useState(false);
  const openPlansModal = useModalStore((state) => state.openPlansModal);
  
  // Knowledge tooltip
  const { isOpen: isTooltipOpen, showTooltip, hideTooltip, hasSeenTooltip } = useKnowledgeTooltipStore();
  const tooltipTimerRef = useRef(null);
  const [blackFridayModalDismissed, setBlackFridayModalDismissed] = useState(false);

  useEffect(() => {
    const initialGroups = {};
    chatbotNavItems.forEach((item) => {
      if (item.type === 'group') {
        initialGroups[item.label] = true;
      }
    });
    setGroups(initialGroups);
  }, []);

  // Listen for Black Friday modal dismissal event
  useEffect(() => {
    const handleBlackFridayDismissed = () => {
      setBlackFridayModalDismissed(true);
    };

    window.addEventListener('blackFridayModalDismissed', handleBlackFridayDismissed);
    return () => {
      window.removeEventListener('blackFridayModalDismissed', handleBlackFridayDismissed);
    };
  }, []);

  // Show knowledge tooltip for first-time users after Black Friday modal
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const isFirstTimeFromURL = urlParams.get('first_time') === 'true';
    const isFirstTimeUser = isFirstTimeFromURL;
    
    const blackFridayModalDismissedInSession = sessionStorage.getItem('blackFridayModalAutoOpened') === 'true';
    const blackFridayHandled = 
      user?.seen_black_friday_modal === true || 
      blackFridayModalDismissedInSession ||
      blackFridayModalDismissed;
    
    const tooltipNotSeen = !hasSeenTooltip();
    
    if (isFirstTimeUser && blackFridayHandled && tooltipNotSeen && !isTooltipOpen) {
      if (isFirstTimeFromURL) {
        const newUrl = new URL(window.location.href);
        window.history.replaceState({}, '', newUrl.toString());
      }
      
      tooltipTimerRef.current = setTimeout(() => {
        showTooltip();
      }, 1500);
    }

    return () => {
      if (tooltipTimerRef.current) {
        clearTimeout(tooltipTimerRef.current);
      }
    };
  }, [auth?.login_count, user?.seen_black_friday_modal, user?.black_friday_offer_type, isTooltipOpen, showTooltip, hasSeenTooltip, location.search, blackFridayModalDismissed]);

  const hasPermission = (permission) => {
    if (Array.isArray(permission)) {
      return permission.some((perm) => checkTeamManagementPermission(perm));
    }
    return checkTeamManagementPermission(permission);
  };

  const handleDeleteChatbot = async () => {
    try {
      setDeleteLoading(true);
      const response = await deleteChatbot(id);
      if (response.status === 200) {
        setShowDeleteModal(false);
        const profileResponse = await getUserProfile();
        if(profileResponse.status === 200){
          setUser(profileResponse.data);
        }
        navigate('/');
      }
    } catch (error) {
      console.error('Error deleting AI Chatbot:', error);
      setDeleteLoading(false);
    } finally {
      setDeleteLoading(false);
    }
  };



  // Define the unified navigation structure
  const navigationItems = [
    {
      type: 'item',
      label: 'Dashboard',
      icon: <DashboardIcon />,
      link: '/dashboard',
      id: 'dashboard',
      permission: 'insights'
    },
  ];

  const allNavItems = [
    {
      label: 'Preview',
      icon: <PreviewIcon />,
      link: '/preview',
      id: 'preview',
      permission: ''
    },
    {
      label: 'Knowledge',
      icon: <MemoryIcon />,
      link: '/knowledge',
      id: 'knowledge',
      permission: 'chatbot_knowledge'
    },
    {
      label: 'Personalities',
      icon: <BehaviorIcon />,
      link: '/personality',
      id: 'personality',
      permission: 'chatbot_personality'
    },
    {
      label: 'Styling',
      icon: <StylingIcon />,
      link: '/styling',
      id: 'styling',
      permission: 'chatbot_styling'
    },
    {
      label: 'Hub & Chat',
      icon: <HubIcon />,
      link: '/tabs',
      id: 'tabs',
      permission: 'tabs'
    },
    {
      label: 'Core Settings',
      icon: <ConfigurationIcon />,
      link: '/setup',
      id: 'setup',
      permission: 'chatbot_core_settings'
    },
    {
      label: 'Safety',
      icon: <SafetyIcon />,
      link: '/safety',
      id: 'safety',
      permission: 'chatbot_safety'
    },
    {
      label: 'Power-Ups',
      icon: <PowerUpsIcon />,
      link: '/powerups',
      id: 'powerups',
      permission: 'chatbot_powerups'
    },
    {
      label: 'Integrations',
      icon: <IntegrationIcon />,
      link: '/integrations',
      id: 'integrations',
      permission: ['api_key', 'integrations']
    },
    {
      label: 'Insights',
      icon: <GraphIcon />,
      link: '/insights',
      id: 'insights',
      permission: 'insights'
    },
   
    {
      label: 'RealTime Voice',
      icon: <VoiceIcon />,
      link: '/realtime-voice',
      id: 'realtime-voice',
      tierFeature: 'realtime_voice_access'
    },

     {
      label: 'Deployments',
      icon: <ShareLineIcon />,
      link: '/deployments',
      id: 'deployments',
      permission: 'share_chatbots'
    },
  ];



  const NavItem = ({ item, isActive, onClick }) => (
    <div
      className={`flex pr-v3-sm rounded-v3-sm items-center gap-2 self-stretch  cursor-pointer hover:bg-v3-menu-hover ${
        isActive ? 'bg-v3-menu-selected' : ''
      } p-v3-sm md:py-[10px] md:pl-[12px]`}
      onClick={onClick}
    >
      <div className=" flex items-center justify-center text-v3-purple-primary">
        <IconWrapper size={20}>
          {item.icon}
        </IconWrapper>
      </div>
      <span className="text-v3-black-max text-center  text-v3-h10 font-[510] whitespace-nowrap">
        {item.label}
      </span>
    </div>
  );



  return (
    <div className="chatbot_nav_wrapper flex flex-col gap-0 w-full md:w-[220px] h-full px-v3-sm pt-v3-sm md:pt-v3-xxs pb-v3-sm">

      {/* Workspace back button - visible on medium screens and up */}
      <div className="hidden md:block px-[12px] py-[10px]">
        <Button
          icon={LeftArrowIcon}
          label="Workspace"
          onClick={() => navigate('/dashboard')}
          iconColor="!text-v3-purple-primary group-hover:!text-v3-purple-medium"
          className="group w-full justify-start !bg-transparent border-0 !text-v3-purple-primary !px-0 !py-0 h-[40px] !text-v3-h10 !font-[510] hover:!text-v3-purple-medium !gap-v3-xs"
        />
      </div>

      {/* Main navigation section */}
      <div className="flex flex-col gap-0 overflow-y-auto no-scrollbar flex-grow relative">
        {/* Dashboard */}
        <NavItem
          item={navigationItems[0]}
          isActive={location.pathname === `/chatbot/${id}` || location.pathname === `/chatbot/${id}/dashboard`}
          onClick={() => {
            setCurrentStep('dashboard');
            navigate(`/chatbot/${id}/dashboard`);
            closeMobileNav && closeMobileNav();
          }}
        />

        {/* Navigation Items */}
        {allNavItems.map((item) => {
          if (item.permission && !hasPermission(item.permission)) {
            return null;
          }
          if (item.tierFeature && !checkFeatureAvailability(item.tierFeature)) {
            return null;
          }

          const isActive = location.pathname.endsWith(item.link) ||
            (item.id === 'insights' && location.pathname.startsWith(`/chatbot/${id}/insights`)) ||
            (item.id === 'integrations' && location.pathname.startsWith(`/chatbot/${id}/integrations`)) ||
            (item.id === 'personality' && location.pathname.startsWith(`/chatbot/${id}/personality`)) ||
            (item.id === 'personality' && location.pathname.startsWith(`/chatbot/${id}/personality/add`)) ||
            (item.id === 'personality' && location.pathname.startsWith(`/chatbot/${id}/personality/:personalityId/edit`));

          return (
            <NavItem
              key={item.id}
              item={item}
              isActive={isActive}
              onClick={() => {
                setCurrentStep(item.id);
                navigate(`/chatbot/${id}${item.link}`);
                closeMobileNav && closeMobileNav();
              }}
            />
          );
        })}

        {/* Knowledge tooltip for first-time users */}
        {/* <KnowledgeTooltip
          isOpen={isTooltipOpen}
          onClose={hideTooltip}
        /> */}
      </div>

      {/* Bottom actions section - this should always stay at the bottom */}
      {/* <div className="mt-auto flex flex-col gap-v3-xs">
        <ChatbotActions
          chatbotId={id}
          selectedChatbot={selectedChatbot}
          variant="desktop"
          closeMobileNav={closeMobileNav}
        />
      </div> */}

      <div className="mt-auto flex flex-col gap-v3-xs">
        <CreditsAndUpgrade
          used={user?.credits_key?.credits_used}
          total={user?.credits_key?.credits_available}
          onUpgrade={() => openPlansModal()}
        />
      </div>

      <DModalShareChatbot
        kb_id={id}
        llm_model={selectedChatbot?.last_model_used?.value || LLM_MODEL_DEFAULT.value}
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
      />
      <DConfirmationModal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteChatbot}
        title="Delete an agent?"
        description="Are you sure you want to delete this agent? This action is irreversible."
        confirmText="Delete"
        cancelText="Cancel"
        variantConfirm="danger"
        loading={deleteLoading}
      />
    </div>
  );
};

export default DChatbotSidebar;
